import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  /** When false, clicking the backdrop does not close (use for destructive flows). */
  closeOnBackdropClick?: boolean;
  /** When false, Escape and the header close control do nothing (e.g. while a mutation runs). */
  allowDismiss?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer,
  closeOnBackdropClick = true,
  allowDismiss = true,
}: ModalProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && allowDismiss) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, allowDismiss]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-6xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          if (allowDismiss && closeOnBackdropClick) onClose();
        }}
      />
      
      {/* Modal */}
      <div
        className={cn(
          'relative w-full bg-[#1a1a24] border border-[#2a2a3a] rounded-xl shadow-2xl',
          'animate-fade-in',
          sizes[size]
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a3a]">
          <h2 className="text-lg font-semibold text-[#f8fafc]">{title}</h2>
          <button
            type="button"
            onClick={() => {
              if (allowDismiss) onClose();
            }}
            disabled={!allowDismiss}
            className="text-[#64748b] hover:text-[#f8fafc] transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">{children}</div>
        
        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2a2a3a]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
